// Port of the mobile core fan (MapPlanner-Mobile packages/core/src/markerFan.ts): markers closer than
// `radiusMeters` form a group, each leans around its own tip on a stem, the middle one on top.
// Same grouping as the app; wider lean and longer stem on purpose (46 px web bubbles overlapped at
// the app's 45° / 11 px) — a display tuning, not a shared constant.
export const MARKER_FAN_DEFAULTS = {
    radiusMeters: 18,
    stepDeg: 60,
    maxSpreadDeg: 150,
    stemPx: 22
};

const UPRIGHT = {angleDeg: 0, stemPx: 0, zIndex: 0, index: 0, count: 1};

const _haversineMeters = (a, b) => {
    const rad = Math.PI / 180;
    const dLat = (b.lat - a.lat) * rad;
    const dLng = (b.lng - a.lng) * rad;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;

    return 2 * 6371000 * Math.asin(Math.sqrt(h));
};

// Pixel offset ([x, y], y up negative — MapLibre Marker `offset`) that puts the stem's foot back on
// the coordinate while keeping the marker's box centred on the leaning pin.
export const markerFanOffset = (slot, contentHeightPx) => {
    if (slot.count < 2) {
        return [0, 0];
    }

    const rad = (slot.angleDeg * Math.PI) / 180;
    const half = contentHeightPx / 2;
    const arm = slot.stemPx + half;

    return [Math.round(arm * Math.sin(rad)), Math.round(half - arm * Math.cos(rad))];
};

// Slots for `coords` ({lat, lng}), in the same order; a marker with no neighbour stays upright.
export const fanColocatedMarkers = (coords, options = {}) => {
    const {radiusMeters, stepDeg, maxSpreadDeg, stemPx} = {...MARKER_FAN_DEFAULTS, ...options};

    const anchors = [];
    const sizes = [];
    const groupOf = [];
    const rankOf = [];

    for (const coord of coords) {
        let group = anchors.findIndex((anchor) => _haversineMeters(anchor, coord) <= radiusMeters);
        if (group < 0) {
            anchors.push(coord);
            sizes.push(0);
            group = anchors.length - 1;
        }

        rankOf.push(sizes[group]);
        sizes[group] += 1;
        groupOf.push(group);
    }

    return coords.map((_, i) => {
        const count = sizes[groupOf[i]];
        if (count < 2) {
            return UPRIGHT;
        }

        const step = Math.min(stepDeg, maxSpreadDeg / (count - 1));
        const rank = rankOf[i];

        return {
            angleDeg: (rank - (count - 1) / 2) * step,
            stemPx,
            zIndex: count - Math.abs(2 * rank - (count - 1)),
            index: rank,
            count
        };
    });
};
